import { Button, NumberBox, SelectBox, TextBox } from "devextreme-react";
import TagBox from 'devextreme-react/tag-box';
import RadioGroup from 'devextreme-react/radio-group';
import { fetcher } from "../../actions";
import useSWR from "swr";
import { Delete } from "@mui/icons-material";
import IconButton from '@mui/material/IconButton';
import { textOptions, numericOptions, columnsValueOptions, logicalOperatorOptions } from "./ColumnOptions"
import { SearchSelectBox } from "./SearchSelectBox";
import { FilterNavigationHeader } from './FilterNavigationHeader';

// Constants
const SPECIAL_COLUMN_IDS = {
    PREFERRED_VENDOR: 5,
    UNIVERSAL_PRODUCT_TYPE: 6,
    PROGRAM_TYPE: 91,
    CATEGORY_TYPE: 92,
    TOP_BUYER_NAME: 94
};

const OPERATORS = {
    IN_LIST: 9,
    NOT_IN_LIST: 10,
    BETWEEN: 9 // for numeric
};

const GRID_ID = 1;

export const ColumnsFilter = (props) => {

    const { 
        filterRows, 
        setFilterRows, 
        setFilterName, 
        setSelectedSearch,
        onNext,
        onBack,
        currentStep = 2,
        totalSteps = 3,
        stepTitle = "Filter Configuration"
    } = props;

    const { data: dataSource } = useSWR(`/api/RoleColumns/reportColumns`, fetcher);

    const preferredVendorRow = filterRows?.filter((row) => row.id === SPECIAL_COLUMN_IDS.PREFERRED_VENDOR);
    const universalProductTypeRow = filterRows?.filter((row) => row.id === SPECIAL_COLUMN_IDS.UNIVERSAL_PRODUCT_TYPE);
    const topBuyerNameRow = filterRows?.filter((row) => row.id === SPECIAL_COLUMN_IDS.TOP_BUYER_NAME);
    const programTypeRow = filterRows?.filter((row) => row.id === SPECIAL_COLUMN_IDS.PROGRAM_TYPE);
    const categoryTypeRow = filterRows?.filter((row) => row.id === SPECIAL_COLUMN_IDS.CATEGORY_TYPE);
    const shouldFetchPreferredVendor = preferredVendorRow[0]?.operator === OPERATORS.IN_LIST || preferredVendorRow[0]?.operator === OPERATORS.NOT_IN_LIST;
    const shouldFetchTopBuyerName = topBuyerNameRow[0]?.operator === OPERATORS.IN_LIST || topBuyerNameRow[0]?.operator === OPERATORS.NOT_IN_LIST;
    const shouldFetchUniversalProductType = universalProductTypeRow[0]?.operator === OPERATORS.IN_LIST || universalProductTypeRow[0]?.operator === OPERATORS.NOT_IN_LIST;
    const shouldFetchPrograms = programTypeRow[0]?.operator === OPERATORS.IN_LIST || programTypeRow[0]?.operator === OPERATORS.NOT_IN_LIST;
    const shouldFetchCategories = categoryTypeRow[0]?.operator === OPERATORS.IN_LIST || categoryTypeRow[0]?.operator === OPERATORS.NOT_IN_LIST;

    const { data: preferredVendorValues } = useSWR(shouldFetchPreferredVendor ? "/api/ValidateItems/GetColumnsItems?columnName=PreferredVendor" : null, fetcher);
    const { data: universalProductTypeValues } = useSWR(shouldFetchUniversalProductType ? "/api/ValidateItems/GetColumnsItems?columnName=universalProductType" : null, fetcher);
    const { data: topBuyerNameValues } = useSWR(shouldFetchTopBuyerName ? "/api/ValidateItems/GetColumnsItems?columnName=TopBuyerName" : null, fetcher);
    const { data: programValues } = useSWR(shouldFetchPrograms ? "/api/Programs" : null, fetcher);
    const { data: categoryValues } = useSWR(shouldFetchCategories ? "/api/Categories" : null, fetcher);



    const emptySearch = [
        {
            "id": 0,
            "column": null,
            "columnType": null,
            "operator": null,
            "operatorName": null,
            "filterValue": null,
            "filterValue2": null,
            "logicalOperator": null
        }
    ];




    const preferredVendorOptions = preferredVendorValues?.map((x, index) => {
        return {
            id: index,
            name: x.list,
        }
    });

    const universalProductTypeOptions = universalProductTypeValues?.map((x, index) => {
        return {
            id: index,
            name: x.list,
        }
    });

    const topBuyerNameOptions = topBuyerNameValues?.map((x, index) => {
        return {
            id: index,
            name: x.list,
        }
    });

    const columnOptions = dataSource?.filter(column => column.NameReport !== null && column.GridId === GRID_ID &&
        column.NameColumn !== "Exclusions")?.map(x => {
            return {
                id: x.Id,
                name: x.NameColumn,
                columnType: x.ColumnType,
            }
        });

    const programOptions = programValues?.map((x) => {
        return {
            id: x.programId,
            name: x.name,
        }
    });

    const categoryOptions = categoryValues?.map((x) => {
        return {
            id: x.categoryId,
            name: x.categoryName,
        }
    });

    const handleColumnChange = (e, index) => {
        if (e.event !== undefined && e.value !== null) {
            const newRows = [...filterRows];
            newRows[index].id = e.value;
            newRows[index].column = columnOptions.find(
                a => a.id === e.value
            )?.name;
            newRows[index].columnType = columnOptions.find(
                a => a.id === e.value
            )?.columnType
            setFilterRows(newRows);
        }
    }

    const handleOperatorChange = ({ event, value }, index) => {
        if (event !== undefined && value !== null) {
            const newRows = [...filterRows];
            newRows[index].operator = value;

            const specialColumnIds = Object.values(SPECIAL_COLUMN_IDS);
            const selectedOption = newRows[index].columnType === 'numeric'
                ? numericOptions.find(a => a.id === value)
                : (specialColumnIds.includes(newRows[index].id)
                    ? [...textOptions, ...columnsValueOptions].find(a => a.id === value)
                    : textOptions.find(a => a.id === value));

            const fullValue = selectedOption?.name;
            newRows[index].operatorName = fullValue?.slice(fullValue?.indexOf(" ") + 1);

            setFilterRows(newRows);
        }
    };

    const handleFilterValueChange = (e, index) => {
        if (e.event !== undefined && e.value !== null) {
            const newRows = [...filterRows];
            newRows[index].filterValue = e.component._changedValue;
            setFilterRows(newRows);
        }
    }

    const handleFilterSelectValueChange = (e, index) => {
        let filterValue = e.value;
        if (Array.isArray(e.value)) {
            filterValue = e.value.join(",");
        }

        if (filterValue !== null) {
            const newRows = [...filterRows];
            const previousValue = newRows[index].filterValue;
            if (previousValue !== filterValue) {
                newRows[index].filterValue = filterValue;
                setFilterRows(newRows);
            }
        }
    }

    const handleFilterValue2Change = (e, index) => {
        if (e.event !== undefined && e.value !== null) {
            const newRows = [...filterRows];
            newRows[index].filterValue2 = e.component._changedValue;
            setFilterRows(newRows);
        }
    }

    const handleFilterRowDelete = (index) => {
        const newRows = [...filterRows];
        if (index === newRows?.length - 1 && newRows?.length > 1) {
            newRows[newRows?.length - 2].logicalOperator = null;
        }
        newRows.splice(index, 1);
        setFilterRows(newRows);
    }

    const handleLogicalOperatorChange = (e, index) => {
        if (e.event !== undefined && e.value !== null) {
            const newRows = [...filterRows];
            if (newRows[index].logicalOperator === null) {
                newRows.push({
                    id: 0,
                    column: null,
                    columnType: null,
                    operator: null,
                    operatorName: null,
                    filterValue: null,
                    filterValue2: null,
                    logicalOperator: null,
                });
            }
            newRows[index].logicalOperator = e.value;
            setFilterRows(newRows);
        }
    }

    const getOptionsToSelect = (columnType, columnId) => {
        const specialColumnIds = Object.values(SPECIAL_COLUMN_IDS);
        if (columnType === 'numeric') {
            return numericOptions;
        } else if (specialColumnIds.includes(columnId)) {
            return [...textOptions, ...columnsValueOptions];
        } else {
            return textOptions;
        }
    };

    const renderNumericField = (operator, filterValue, filterValue2, index) => {
        if (operator === OPERATORS.BETWEEN) {
            return (
                <div style={{ display: "flex", flexDirection: "row", gap: "0.5rem", width: "100%" }}>
                    <div style={{ flex: 1 }}>
                        <NumberBox
                            onChange={(e) => handleFilterValue2Change(e, index)}
                            placeholder="Min Value"
                            value={filterValue2}
                            width="100%"
                        />
                    </div>
                    <div style={{ 
                        display: "flex", 
                        alignItems: "center", 
                        padding: "0 0.5rem",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "#6c757d"
                    }}>
                        to
                    </div>
                    <div style={{ flex: 1 }}>
                        <NumberBox
                            onChange={(e) => handleFilterValueChange(e, index)}
                            placeholder="Max Value"
                            value={filterValue}
                            width="100%"
                        />
                    </div>
                </div>
            )
        }
        else {
            return (
                <NumberBox
                    onChange={(e) => handleFilterValueChange(e, index)}
                    placeholder="Enter numeric value"
                    value={filterValue}
                    width="100%"
                />
            )
        }
    }

    const renderStringField = (id, operator, filterValue, index) => {
        let columnOptions = null;
        if (operator === OPERATORS.IN_LIST || operator === OPERATORS.NOT_IN_LIST) {
            switch (id) {
                case SPECIAL_COLUMN_IDS.PREFERRED_VENDOR:
                    columnOptions = preferredVendorOptions;
                    break;
                case SPECIAL_COLUMN_IDS.UNIVERSAL_PRODUCT_TYPE:
                    columnOptions = universalProductTypeOptions;
                    break;
                case SPECIAL_COLUMN_IDS.PROGRAM_TYPE:
                    columnOptions = programOptions;
                    break;
                case SPECIAL_COLUMN_IDS.CATEGORY_TYPE:
                    columnOptions = categoryOptions;
                    break;
                case SPECIAL_COLUMN_IDS.TOP_BUYER_NAME:
                    columnOptions = topBuyerNameOptions;
                    break;
                default:
                    columnOptions = null;
            }

            const initialFilterValue = filterValue?.split(",");

            return (
                <TagBox
                    width="100%"
                    placeholder="Select values from list"
                    dataSource={columnOptions}
                    displayExpr="name"
                    valueExpr="name"
                    value={initialFilterValue}
                    multiline={false}
                    onValueChanged={(e) => handleFilterSelectValueChange(e, index)}
                    showSelectionControls={true}
                    maxDisplayedTags={3}
                />
            )
        }
        else {
            return (
                <TextBox
                    onChange={(e) => handleFilterValueChange(e, index)}
                    placeholder="Enter text value"
                    value={filterValue}
                    width="100%"
                />
            )
        }
    }

    const getActiveFiltersCount = () => {
        return filterRows?.filter(row => row.id && row.operator && (row.filterValue || row.filterValue2)).length || 0;
    };

    const handleDeleteAllFilters = () => {
        setFilterRows(emptySearch);
        setFilterName(null);
        setSelectedSearch(null);
    };

    const handleSelectedSearchChange = (selectedSearchName, selectedSearch) => {
        const filterRows = selectedSearch?.query;
        if (selectedSearchName === null) {
            setFilterRows(emptySearch);
            setFilterName(null);
            setSelectedSearch(null);
            return;
        }
        setFilterRows(filterRows);
        setFilterName(selectedSearchName);
        setSelectedSearch(selectedSearch);
    };
    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden"
        }}>
            {/* Navigation Header */}
            <FilterNavigationHeader
                stepTitle={stepTitle}
                currentStep={currentStep}
                totalSteps={totalSteps}
                activeFiltersCount={getActiveFiltersCount()}
                onBack={onBack}
                onNext={onNext}
                showApply={false}
            />

            {/* Search and Actions Header */}
            <div style={{ 
                display: "flex", 
                flexDirection: "row", 
                justifyContent: "space-between", 
                alignItems: "center",
                padding: "1rem", 
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                marginBottom: "0.75rem", 
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                flexShrink: 0
            }}>
                <div style={{ flex: 1, maxWidth: "400px" }}>
                    <label style={{ 
                        display: "block", 
                        marginBottom: "0.5rem", 
                        fontWeight: "500",
                        color: "#495057",
                        fontSize: "14px"
                    }}>
                        Load Saved Search
                    </label>
                    <SearchSelectBox
                        type={"column"}
                        onSelectedSearchChange={handleSelectedSearchChange}
                    />
                </div>
                
                <div style={{ 
                    display: "flex", 
                    gap: "0.75rem", 
                    marginLeft: "1rem",
                    alignItems: "flex-end"
                }}>
                    {getActiveFiltersCount() > 0 && (
                        <div style={{
                            backgroundColor: "#28a745",
                            color: "white",
                            padding: "0.25rem 0.75rem",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            alignSelf: "center"
                        }}>
                            {getActiveFiltersCount()} Active Filter{getActiveFiltersCount() !== 1 ? 's' : ''}
                        </div>
                    )}
                    <Button
                        icon="clear"
                        text="Clear All Filters"
                        onClick={handleDeleteAllFilters}
                        hint="Remove all current filters"
                        stylingMode="outlined"
                    />
                    <Button
                        icon="plus"
                        text="Add Filter Row"
                        onClick={() => {
                            const newRows = [...filterRows];
                            newRows.push({
                                id: 0,
                                column: null,
                                columnType: null,
                                operator: null,
                                operatorName: null,
                                filterValue: null,
                                filterValue2: null,
                                logicalOperator: null,
                            });
                            setFilterRows(newRows);
                        }}
                        hint="Add a new filter row"
                        stylingMode="contained"
                    />
                </div>
            </div>

            {/* Filter Rows Section */}
            <div style={{ 
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
                flex: 1,
                minHeight: "200px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column"
            }}>
                <div 
                    style={{ 
                        padding: "0.75rem",
                        flex: 1,
                        maxHeight: "30vh",
                        overflowY: "auto",
                        overflowX: "hidden"
                    }}
                    className="filter-rows-scrollable"
                >
                    <style jsx>{`
                        .filter-rows-scrollable {
                            scrollbar-width: thin;
                            scrollbar-color: #007bff #f8f9fa;
                        }
                        .filter-rows-scrollable::-webkit-scrollbar {
                            width: 8px;
                        }
                        .filter-rows-scrollable::-webkit-scrollbar-track {
                            background: #f8f9fa;
                            border-radius: 4px;
                        }
                        .filter-rows-scrollable::-webkit-scrollbar-thumb {
                            background: #007bff;
                            border-radius: 4px;
                        }
                        .filter-rows-scrollable::-webkit-scrollbar-thumb:hover {
                            background: #0056b3;
                        }
                    `}</style>
                    {filterRows?.length === 0 || (filterRows?.length === 1 && !filterRows[0].id) ? (
                        <div style={{
                            textAlign: "center",
                            padding: "2rem",
                            color: "#6c757d",
                            fontStyle: "italic"
                        }}>
                            No filters configured. Use "Add Filter Row" to create your first filter.
                        </div>
                    ) : (
                        <>
                            {getActiveFiltersCount() > 3 && (
                                <div style={{
                                    backgroundColor: "#fff3cd",
                                    color: "#856404",
                                    padding: "0.5rem",
                                    borderRadius: "4px",
                                    marginBottom: "1rem",
                                    fontSize: "12px",
                                    textAlign: "center",
                                    border: "1px solid #ffeaa7"
                                }}>
                                    📜 Scroll down to see all {getActiveFiltersCount()} filters
                                </div>
                            )}
                            {filterRows?.map((filterRow, index) => (
                        <div key={`filter-row-${index}`} style={{
                            marginBottom: "0.75rem", // Reduced margin
                            padding: "0.75rem", // Reduced padding
                            backgroundColor: index % 2 === 0 ? "#f8f9fa" : "#ffffff",
                            borderRadius: "6px",
                            border: "1px solid #dee2e6"
                        }}>
                            <div style={{ 
                                display: "flex", 
                                flexDirection: "row", 
                                justifyContent: "space-between", 
                                alignItems: "center",
                                gap: "1rem",
                                flexWrap: "wrap"
                            }}>
                                <div style={{ 
                                    display: "flex", 
                                    flexDirection: "column",
                                    minWidth: "200px",
                                    flex: "1"
                                }}>
                                    <label style={{ 
                                        fontSize: "12px", 
                                        fontWeight: "500", 
                                        marginBottom: "0.25rem",
                                        color: "#495057"
                                    }}>Column</label>
                                    <SelectBox
                                        placeholder="Select Column to Filter"
                                        dataSource={columnOptions}
                                        displayExpr="name"
                                        valueExpr="id"
                                        value={filterRow?.id}
                                        onValueChanged={(e) => handleColumnChange(e, index)}
                                        id={`column-${index}`}
                                    />
                                </div>
                                <div style={{ 
                                    display: "flex", 
                                    flexDirection: "column",
                                    minWidth: "180px",
                                    flex: "1"
                                }}>
                                    <label style={{ 
                                        fontSize: "12px", 
                                        fontWeight: "500", 
                                        marginBottom: "0.25rem",
                                        color: "#495057"
                                    }}>Operator</label>
                                    <SelectBox
                                        placeholder="Select Operator"
                                        dataSource={getOptionsToSelect(filterRow?.columnType, filterRow?.id)}
                                        displayExpr="name"
                                        valueExpr="id"
                                        value={filterRow?.operator}
                                        onValueChanged={(e) => handleOperatorChange(e, index)}
                                        id={`operator-${index}`}
                                    />
                                </div>
                                <div style={{ 
                                    display: "flex", 
                                    flexDirection: "column",
                                    minWidth: "200px",
                                    flex: "2"
                                }}>
                                    <label style={{ 
                                        fontSize: "12px", 
                                        fontWeight: "500", 
                                        marginBottom: "0.25rem",
                                        color: "#495057"
                                    }}>Value</label>
                                    <div style={{ display: "flex", flexDirection: "row" }}>
                                        {filterRow?.columnType === 'numeric' ?
                                            renderNumericField(filterRow?.operator, filterRow?.filterValue, filterRow?.filterValue2, index) :
                                            renderStringField(filterRow?.id, filterRow?.operator, filterRow?.filterValue, index)}
                                    </div>
                                </div>
                                <div style={{ 
                                    display: "flex", 
                                    flexDirection: "column",
                                    minWidth: "120px"
                                }}>
                                    <label style={{ 
                                        fontSize: "12px", 
                                        fontWeight: "500", 
                                        marginBottom: "0.25rem",
                                        color: "#495057"
                                    }}>Logic</label>
                                    <RadioGroup 
                                        items={logicalOperatorOptions} 
                                        value={filterRow?.logicalOperator} 
                                        onValueChanged={(e) => handleLogicalOperatorChange(e, index)} 
                                        valueExpr="id" 
                                        layout="horizontal" 
                                    />
                                </div>
                                <div style={{ 
                                    display: "flex", 
                                    alignItems: "flex-end",
                                    paddingBottom: "0.25rem"
                                }}>
                                    {(index !== 0 || filterRows.length > 1) &&
                                        <IconButton 
                                            id={index} 
                                            onClick={() => handleFilterRowDelete(index)}
                                            style={{
                                                backgroundColor: "#dc3545",
                                                color: "white",
                                                '&:hover': { backgroundColor: "#c82333" }
                                            }}
                                            size="small"
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    }
                                </div>
                            </div>
                            {
                                index < filterRows.length - 1 &&
                                <div style={{
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    margin: "0.25rem 0", // Reduced margin
                                    padding: "0.25rem" // Reduced padding
                                }}>
                                    <div style={{ 
                                        flex: 1, 
                                        height: '2px', 
                                        backgroundColor: '#007bff',
                                        borderRadius: '1px'
                                    }} />
                                    <div style={{
                                        padding: "0.25rem 1rem",
                                        backgroundColor: "#007bff",
                                        color: "white",
                                        borderRadius: "12px",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        margin: "0 1rem"
                                    }}>
                                        {logicalOperatorOptions.find(
                                            a => a.id === filterRow?.logicalOperator
                                        )?.text || 'AND'}
                                    </div>
                                    <div style={{ 
                                        flex: 1, 
                                        height: '2px', 
                                        backgroundColor: '#007bff',
                                        borderRadius: '1px'
                                    }} />
                                </div>
                            }
                        </div>
                    ))
                    }
                    </>
                )}
                </div>
                
                {/* Add Filter Button - Positioned below scrollable area */}
                {getActiveFiltersCount() > 2 && (
                    <div style={{
                        padding: "1rem 0.75rem 0.75rem 0.75rem",
                        borderTop: "1px solid #e9ecef",
                        backgroundColor: "#f8f9fa",
                        display: "flex",
                        justifyContent: "flex-end",
                        flexShrink: 0
                    }}>
                        <Button
                            icon="plus"
                            text="Add Filter"
                            onClick={() => {
                                const newRows = [...filterRows];
                                newRows.push({
                                    id: 0,
                                    column: null,
                                    columnType: null,
                                    operator: null,
                                    operatorName: null,
                                    filterValue: null,
                                    filterValue2: null,
                                    logicalOperator: null,
                                });
                                setFilterRows(newRows);
                            }}
                            width={120}
                            hint="Quick add filter"
                            stylingMode="contained"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
